import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { store } from '../../../../../store/store';
import { checkIfModelRequired, getDirections, getDestinationRichMapId, getDestinationName, getDestinationFloorNumber, setDirections, setDirectionsPanel, } from '../../../../../store/uislice';
import { getContext, getLocation, getSelectedLocation, setSelectedLocation, } from '../../../redux/spaces-slice';
import { getFloorNumberIdMap, getFloorShortNameByNumber } from '../../util';
import { getLocalizedNavigationConnectorName } from '../../util/navigation-connector';
import { invokeRichMapMethod } from '../map/rich-map';
import { closeRoomDetailsPopup } from '../room-details-popup';
import spotIconStepper from '../../../../../assets/images/spot-icon-stepper.svg';
import markerIconStepper from '../../../../../assets/images/marker-icon-stepper.svg';
const Container = styled.div `
  display: flex;
  align-items: stretch;
  gap: clamp(0.18rem, 0.26vw, 0.34rem);
  width: min(55vw, 50rem);
`;
const StepCard = styled.div `
  position: relative;
  min-height: clamp(3rem, 3.3vw, 3.45rem);
  border-radius: clamp(0.55rem, 0.66vw, 0.72rem);
  border: 1px solid
    ${({ $active }) => $active ? 'rgba(101, 170, 255, 0.55)' : 'rgba(116, 131, 149, 0.42)'};
  background: ${({ $active }) => $active
    ? 'linear-gradient(180deg, rgba(44, 131, 242, 0.92) 0%, rgba(33, 102, 194, 0.92) 100%)'
    : 'linear-gradient(180deg, rgba(41, 48, 58, 0.9) 0%, rgba(34, 40, 49, 0.9) 100%)'};
  box-shadow:
    inset 0 1px 0
      ${({ $active }) => $active ? 'rgba(215, 230, 248, 0.28)' : 'rgba(172, 190, 212, 0.16)'},
    ${({ $active }) => $active
    ? '0 0 0 1px rgba(72, 153, 255, 0.18), 0 12px 28px rgba(0, 0, 0, 0.24)'
    : '0 12px 28px rgba(0, 0, 0, 0.24)'};
  display: flex;
  align-items: center;
  padding: 0 clamp(0.56rem, 0.68vw, 0.74rem);
  overflow: visible;
  cursor: pointer;
`;
const StartCard = styled(StepCard) `
  min-width: clamp(10.1rem, 11.7vw, 11.2rem);
  padding-left: clamp(3.05rem, 3.5vw, 3.35rem);
`;
const MiddleCard = styled(StepCard) `
  min-width: clamp(9.2rem, 10.65vw, 10.1rem);
  gap: clamp(0.4rem, 0.48vw, 0.54rem);
`;
const EndCard = styled(StepCard) `
  min-width: clamp(11.5rem, 13.15vw, 12.8rem);
  /* Cap how wide the destination card can get; long names scroll inside it
     (see EndTextScroll) instead of overflowing the bar/container. */
  max-width: clamp(13rem, 16vw, 17rem);
  padding-right: clamp(2.3rem, 2.8vw, 2.55rem);
`;
// Horizontal-scroll region for the destination card's (potentially long) label.
// Fills the card's remaining width (before the marker) and scrolls when the text
// is longer than what fits. Scrollbar hidden (kiosk/touch: swipe to scroll).
const EndTextScroll = styled.div `
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;
const StartMascot = styled.img `
  position: absolute;
  left: clamp(0.18rem, 0.28vw, 0.34rem);
  top: clamp(-1.98rem, -1.42vw, -1.54rem);
  width: clamp(2.25rem, 2.55vw, 2.7rem);
  height: auto;
  z-index: 2;
`;
const StepText = styled.div `
  color: #ffffff;
  font-family: 'Sora', sans-serif;
  font-size: clamp(0.75rem, 0.88vw, 0.92rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  white-space: nowrap;
`;
const Muted = styled.span `
  color: rgba(215, 223, 233, 0.78);
  font-weight: 400;
`;
const IconTile = styled.div `
  width: clamp(1.7rem, 2vw, 2rem);
  height: clamp(1.7rem, 2vw, 2rem);
  border-radius: clamp(0.4rem, 0.48vw, 0.52rem);
  background: ${({ $active }) => $active ? 'rgba(255, 255, 255, 0.18)' : 'rgba(89, 101, 118, 0.62)'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
`;
const UpArrow = styled.span `
  color: #ffffff;
  font-size: clamp(1.15rem, 1.35vw, 1.45rem);
  line-height: 1;
  transform: translateY(-0.08rem);
`;
const EndMarker = styled.img `
  position: absolute;
  right: clamp(0.42rem, 0.52vw, 0.58rem);
  top: clamp(-1rem, -0.68vw, -0.72rem);
  width: clamp(2.05rem, 2.35vw, 2.5rem);
  height: clamp(3rem, 3.35vw, 3.55rem);
  z-index: 2;
`;
const CloseButton = styled.button `
  appearance: none;
  border: 1px solid rgba(116, 131, 149, 0.46);
  background: linear-gradient(
    180deg,
    rgba(41, 48, 58, 0.9) 0%,
    rgba(34, 40, 49, 0.9) 100%
  );
  box-shadow:
    inset 0 1px 0 rgba(172, 190, 212, 0.16),
    0 12px 28px rgba(0, 0, 0, 0.24);
  color: #ffffff;
  width: clamp(2.7rem, 3vw, 3rem);
  min-height: clamp(3rem, 3.3vw, 3.45rem);
  border-radius: clamp(0.55rem, 0.66vw, 0.72rem);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
`;
const CloseGlyph = styled.div `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-family: 'Sora', sans-serif;
  font-size: 1.3rem;
  font-weight: 600;
  line-height: 1;
`;
const MULTIFLOOR_NAVIGATION_DELAY_MS = 420;
const SOURCE_LEVEL_ROUTE_REDRAW_DELAY_MS = 200;
const MultifloorDirectionsBar = ({ activeRichMapFloorNumber, ready, }) => {
    var _a;
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const richMapId = useSelector(getDestinationRichMapId);
    // Authoritative destination data captured when directions started. Falls back
    // to parsing the rich map id only when these weren't provided (legacy path).
    const storedDestinationName = useSelector(getDestinationName);
    const storedDestinationFloorNumber = useSelector(getDestinationFloorNumber);
    const selectedFloorId = useSelector(getSelectedLocation);
    const directionsData = useSelector(getDirections);
    const locationData = useSelector(getLocation);
    const deviceLocationId = (_a = useSelector(getContext)) === null || _a === void 0 ? void 0 : _a.deviceLocationId;
    const floorLocationData = locationData === null || locationData === void 0 ? void 0 : locationData.children.find((child) => child.id === deviceLocationId);
    const sourceFloorNumber = Number(floorLocationData === null || floorLocationData === void 0 ? void 0 : floorLocationData.floorNumber);
    const destinationFloorNumber = storedDestinationFloorNumber !== null && storedDestinationFloorNumber !== void 0 ? storedDestinationFloorNumber : Number(richMapId === null || richMapId === void 0 ? void 0 : richMapId.split('~')[0]);
    const destinationFloorShortName = getFloorShortNameByNumber(locationData, destinationFloorNumber);
    const destinationName = storedDestinationName !== null && storedDestinationName !== void 0 ? storedDestinationName : ((richMapId === null || richMapId === void 0 ? void 0 : richMapId.split('~')[1]) || '');
    // `null` while the SDK route data (transitionInfo) hasn't arrived yet.
    const connectorName = getLocalizedNavigationConnectorName(directionsData, t);
    // Stairs/Elevator once resolved; Elevator is the sole fallback (used when the
    // parent's readiness timeout elapses before the route data arrives).
    const connectorLabel = connectorName !== null && connectorName !== void 0 ? connectorName : t('floor.directions.elevator');
    const floorNumberIdMap = getFloorNumberIdMap(locationData);
    const sourceFloorId = deviceLocationId || floorNumberIdMap[sourceFloorNumber];
    const destinationFloorId = floorNumberIdMap[destinationFloorNumber];
    const richMap = window.rMap;
    const [activeStep, setActiveStep] = React.useState('start');
    const preparedRichMapIdRef = React.useRef(null);
    const navigatedRichMapIdRef = React.useRef(null);
    const currentLevelRef = React.useRef('source');
    const startNavigationTimeoutRef = React.useRef(null);
    const redrawSourceLevelRoute = React.useCallback(() => {
        if (!richMapId) {
            return;
        }
        if (startNavigationTimeoutRef.current) {
            window.clearTimeout(startNavigationTimeoutRef.current);
        }
        startNavigationTimeoutRef.current = window.setTimeout(() => {
            startNavigationTimeoutRef.current = null;
            invokeRichMapMethod('reset');
            invokeRichMapMethod('navigate', richMapId);
            currentLevelRef.current = 'source';
            store.dispatch(checkIfModelRequired());
        }, SOURCE_LEVEL_ROUTE_REDRAW_DELAY_MS);
    }, [richMapId]);
    React.useEffect(() => {
        if (!richMapId || !sourceFloorId || !richMap) {
            return;
        }
        if (preparedRichMapIdRef.current === richMapId) {
            return;
        }
        preparedRichMapIdRef.current = richMapId;
        navigatedRichMapIdRef.current = null;
        currentLevelRef.current = 'source';
        setActiveStep('start');
        if (selectedFloorId !== sourceFloorId) {
            dispatch(setSelectedLocation(sourceFloorId));
        }
    }, [dispatch, richMap, richMapId, selectedFloorId, sourceFloorId]);
    React.useEffect(() => {
        if (!richMapId ||
            !sourceFloorId ||
            !richMap ||
            navigatedRichMapIdRef.current === richMapId ||
            preparedRichMapIdRef.current !== richMapId ||
            selectedFloorId !== sourceFloorId ||
            activeRichMapFloorNumber !== sourceFloorNumber) {
            return;
        }
        startNavigationTimeoutRef.current = window.setTimeout(() => {
            startNavigationTimeoutRef.current = null;
            if (preparedRichMapIdRef.current !== richMapId ||
                selectedFloorId !== sourceFloorId ||
                activeRichMapFloorNumber !== sourceFloorNumber) {
                return;
            }
            invokeRichMapMethod('reset');
            invokeRichMapMethod('navigate', richMapId);
            navigatedRichMapIdRef.current = richMapId;
        }, MULTIFLOOR_NAVIGATION_DELAY_MS);
        return () => {
            if (startNavigationTimeoutRef.current) {
                window.clearTimeout(startNavigationTimeoutRef.current);
                startNavigationTimeoutRef.current = null;
            }
        };
    }, [
        activeRichMapFloorNumber,
        richMap,
        richMapId,
        selectedFloorId,
        sourceFloorId,
        sourceFloorNumber,
    ]);
    React.useEffect(() => {
        if (!selectedFloorId ||
            !sourceFloorId ||
            !destinationFloorId ||
            !richMapId) {
            return;
        }
        if (selectedFloorId === destinationFloorId &&
            activeRichMapFloorNumber === destinationFloorNumber) {
            if (currentLevelRef.current !== 'destination') {
                invokeRichMapMethod('nextNavigationLevel');
                currentLevelRef.current = 'destination';
            }
            setActiveStep('end');
            store.dispatch(checkIfModelRequired());
            return;
        }
        if (selectedFloorId === sourceFloorId &&
            activeRichMapFloorNumber === sourceFloorNumber) {
            if (currentLevelRef.current !== 'source') {
                redrawSourceLevelRoute();
            }
            else {
                store.dispatch(checkIfModelRequired());
            }
            setActiveStep((current) => (current === 'end' ? 'middle' : current));
        }
    }, [
        activeRichMapFloorNumber,
        destinationFloorId,
        destinationFloorNumber,
        redrawSourceLevelRoute,
        richMapId,
        selectedFloorId,
        sourceFloorId,
        sourceFloorNumber,
    ]);
    const goToSource = () => {
        setActiveStep('start');
        if (!sourceFloorId) {
            return;
        }
        if (selectedFloorId !== sourceFloorId) {
            dispatch(setSelectedLocation(sourceFloorId));
            return;
        }
        if (activeRichMapFloorNumber !== sourceFloorNumber) {
            return;
        }
        if (currentLevelRef.current !== 'source') {
            redrawSourceLevelRoute();
            return;
        }
        store.dispatch(checkIfModelRequired());
    };
    const goToMiddle = () => {
        setActiveStep('middle');
        if (!sourceFloorId) {
            return;
        }
        if (selectedFloorId !== sourceFloorId) {
            dispatch(setSelectedLocation(sourceFloorId));
            return;
        }
        if (activeRichMapFloorNumber !== sourceFloorNumber) {
            return;
        }
        if (currentLevelRef.current !== 'source') {
            redrawSourceLevelRoute();
            return;
        }
        store.dispatch(checkIfModelRequired());
    };
    const goToDestination = () => {
        setActiveStep('end');
        if (!destinationFloorId) {
            return;
        }
        if (selectedFloorId !== destinationFloorId) {
            dispatch(setSelectedLocation(destinationFloorId));
            return;
        }
        if (activeRichMapFloorNumber !== destinationFloorNumber) {
            return;
        }
        if (currentLevelRef.current !== 'destination') {
            invokeRichMapMethod('nextNavigationLevel');
            currentLevelRef.current = 'destination';
            store.dispatch(checkIfModelRequired());
        }
    };
    const closeHandler = () => {
        store.dispatch(setDirectionsPanel({ payload: null }));
        store.dispatch(setDirections({ payload: null }));
        const wasOnDestinationLevel = currentLevelRef.current === 'destination';
        preparedRichMapIdRef.current = null;
        navigatedRichMapIdRef.current = null;
        if (startNavigationTimeoutRef.current) {
            window.clearTimeout(startNavigationTimeoutRef.current);
            startNavigationTimeoutRef.current = null;
        }
        setActiveStep('start');
        if (wasOnDestinationLevel) {
            if (!invokeRichMapMethod('prevNavigationLevel')) {
                invokeRichMapMethod('exitNavigation');
            }
        }
        currentLevelRef.current = 'source';
        if (sourceFloorId) {
            dispatch(setSelectedLocation(sourceFloorId));
        }
        else {
            dispatch(setSelectedLocation(floorNumberIdMap[sourceFloorNumber]));
        }
        setTimeout(() => {
            store.dispatch(checkIfModelRequired());
        }, 600);
        closeRoomDetailsPopup();
        setTimeout(() => {
            invokeRichMapMethod('exitNavigation');
            invokeRichMapMethod('reset');
        }, 1000);
    };
    if (!richMapId || destinationFloorNumber === sourceFloorNumber) {
        return null;
    }
    // Not ready yet (route data still loading, per the parent): render nothing so
    // the parent's action buttons stay visible. The component stays mounted, so
    // its navigation effects keep firing and trigger the SDK route. The stepper
    // appears once `ready` flips (connector resolved or the parent's 5s timeout).
    if (!ready) {
        return null;
    }
    return (_jsxs(Container, { children: [_jsxs(StartCard, Object.assign({ "$active": activeStep === 'start', onClick: goToSource }, { children: [_jsx(StartMascot, { src: spotIconStepper, alt: t('floor.directions.spot') }), _jsxs(StepText, { children: [t('floor.directions.spot'), " ", t('floor.directions.to-lower'), ' ', connectorLabel] })] })), _jsxs(MiddleCard, Object.assign({ "$active": activeStep === 'middle', onClick: goToMiddle }, { children: [_jsx(IconTile, Object.assign({ "$active": activeStep === 'middle' }, { children: _jsx(UpArrow, { children: "\u2191" }) })), _jsxs(StepText, { children: [connectorLabel, " ", _jsx(Muted, { children: t('floor.directions.to-lower') }), ' ', destinationFloorShortName] })] })), _jsxs(EndCard, Object.assign({ "$active": activeStep === 'end', onClick: goToDestination }, { children: [_jsx(EndTextScroll, { children: _jsxs(StepText, { children: [connectorLabel, " ", _jsx(Muted, { children: t('floor.directions.to-lower') }), ' ', destinationName] }) }), _jsx(EndMarker, { src: markerIconStepper, alt: "Destination marker" })] })), _jsx(CloseButton, Object.assign({ onClick: closeHandler }, { children: _jsx(CloseGlyph, { children: "x" }) }))] }));
};
export default MultifloorDirectionsBar;
